// src/components/cart/CartPageClient.tsx
"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import Section from "../ui/Section";
import Wrapper from "../ui/Wrapper";
import Link from "next/link";
import { Button } from "../ui/Button";
import { useCart } from "./cart-context";
import { useLayoutData } from "../layout/LayoutDataProvider";
import { LANGUAGES, DEFAULT_LANGUAGE } from "@/lib/languages";
import SafeHtml from "../ui/SafeHtml";
import { apiPath } from "@/lib/apiPath";

type Props = {
  lang: string;
  basePath?: string;
  initialCart?: unknown | null;
};

type CartItem = {
  key?: string;
  name?: string;
  permalink?: string;
  quantity?: number;
  quantity_limits?: {
    minimum?: number;
    maximum?: number;
    multiple_of?: number;
    editable?: boolean;
  };
  prices?: {
    price?: string | number;
    currency_code?: string;
    currency_minor_unit?: number;
  };
  totals?: {
    line_total?: string | number;
    total?: string | number;
    currency_code?: string;
    currency_minor_unit?: number;
  };
  item_data?: Array<{ key?: string; value?: unknown }>;
  extensions?: Record<string, unknown>;
  meta_data?: Array<{ key?: string; value?: unknown }>;
  backorders_allowed?: boolean;
  sold_individually?: boolean;
  low_stock_remaining?: number | null;
};

type Cart = {
  items?: CartItem[];
  items_count?: number;
  totals?: {
    total_items?: string | number | null;
    total_price?: string | number | null;
    total?: string | number | null;
    currency_code?: string;
    currency_minor_unit?: number;
  } | null;
};

type PendingMutation = {
  key: string;
  quantity: number;
};

type MutationErrorPayload = {
  error?: string;
  message?: string;
  cart?: Cart | null;
};

function toInt(n: unknown, fallback = 0) {
  const x = Number(n);
  return Number.isFinite(x) ? Math.floor(x) : fallback;
}

function toMinorNumber(v: unknown): number | null {
  if (v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isFinite(n) ? Math.round(n) : null;
}

function decodeHtmlEntities(input: string) {
  if (typeof document === "undefined") return input;
  const txt = document.createElement("textarea");
  txt.innerHTML = input;
  return txt.value;
}

async function getErrorMessageFromResponse(
  res: Response,
  fallbackTemplate: string
) {
  const text = await res.text().catch(() => "");
  try {
    const json = JSON.parse(text) as { message?: unknown; error?: unknown };
    if (typeof json.message === "string" && json.message.trim()) {
      return decodeHtmlEntities(json.message);
    }
    if (typeof json.error === "string" && json.error.trim()) {
      return decodeHtmlEntities(json.error);
    }
  } catch {
    // ignore
  }
  return text ? text.slice(0, 300) : fallbackTemplate.replace("{status}", String(res.status));
}

function safeStr(v: unknown) {
  return typeof v === "string" ? v : "";
}

function resolveLocaleFromLang(lang: string) {
  const raw = (lang || "").trim();
  if (!raw) return DEFAULT_LANGUAGE.polylangLocale;

  const lower = raw.toLowerCase();

  const byCode = LANGUAGES.find((l) => l.polylangCode.toLowerCase() === lower);
  if (byCode) return byCode.polylangLocale;

  const byPreferred = LANGUAGES.find(
    (l) =>
      Array.isArray(l.preferredBy) &&
      l.preferredBy.some((x) => String(x).toLowerCase() === lower)
  );
  if (byPreferred) return byPreferred.polylangLocale;

  return raw;
}

function formatISODate(iso: string, locale: string) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return iso;

  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);

  const dt = new Date(Date.UTC(y, mo - 1, d));
  if (Number.isNaN(dt.getTime())) return iso;

  return new Intl.DateTimeFormat(locale, {
    year: "numeric",
    month: "long",
    day: "2-digit",
  }).format(dt);
}

function formatWooMoney(
  value: string | number,
  currencyCode: string,
  minorUnit: number,
  locale: string
) {
  const minor = typeof value === "string" ? Number(value) : value;
  const major = minor / Math.pow(10, minorUnit);

  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: currencyCode,
    minimumFractionDigits: minorUnit,
    maximumFractionDigits: minorUnit,
  }).format(major);
}

function formatMoneyFromValue(
  value: string | number | null | undefined,
  currencyCode: string | null | undefined,
  minorUnit: number | null | undefined,
  locale: string
) {
  if (value === null || value === undefined) return "—";
  if (!currencyCode) return "—";
  if (typeof minorUnit !== "number" || !Number.isFinite(minorUnit)) return "—";
  return formatWooMoney(value, currencyCode, minorUnit, locale);
}

function parseSlotFromItemData(
  it: CartItem
): { label: string; dateISO: string; time: string } | null {
  const itemData = it.item_data;
  if (!Array.isArray(itemData) || itemData.length === 0) return null;

  const ts = itemData.find((x) => safeStr(x?.key).toLowerCase() === "time slot");
  const v = ts?.value;

  if (typeof v !== "string" || !v.trim()) return null;

  const parts = v
    .split("•")
    .map((s) => s.trim())
    .filter(Boolean);

  const label = parts[0] ?? "";
  const dateISO = parts.find((p) => /^\d{4}-\d{2}-\d{2}$/.test(p)) ?? "";
  const time =
    [...parts].reverse().find((p) => /^\d{1,2}:\d{2}(\s?[ap]m)?$/i.test(p)) ?? "";

  if (!label && !dateISO && !time) return null;
  return { label, dateISO, time };
}

function getSlotSummaryFallback(it: CartItem): string | null {
  const ext = it.extensions;
  const extCandidates: unknown[] = [];

  if (ext && typeof ext === "object") {
    const o = ext as Record<string, unknown>;
    extCandidates.push(o["fooevents"]);
    extCandidates.push(o["FooEvents"]);
    extCandidates.push(o["bright_bookings"]);
    extCandidates.push(o["brightBookings"]);
    extCandidates.push(o["bookings"]);
  }

  for (const c of extCandidates) {
    if (!c || typeof c !== "object") continue;
    const obj = c as Record<string, unknown>;

    const slotLabel = safeStr(obj["slot_label"] ?? obj["slotLabel"] ?? obj["label"]);
    const date = safeStr(obj["date"]);
    const time = safeStr(obj["time"]);

    const parts = [slotLabel, date, time].filter(Boolean);
    if (parts.length) return parts.join(" • ");
  }

  const meta = it.meta_data;
  if (Array.isArray(meta) && meta.length) {
    const readMeta = (key: string) => {
      const found = meta.find((m) => safeStr(m?.key).toLowerCase() === key.toLowerCase());
      if (!found) return "";
      const v = found.value;
      if (typeof v === "string") return v;
      if (typeof v === "number") return String(v);
      try {
        return JSON.stringify(v);
      } catch {
        return "";
      }
    };

    const label = readMeta("fooevents_slot_label") || readMeta("slot_label");
    const date = readMeta("fooevents_date") || readMeta("slot_date");
    const time = readMeta("fooevents_time") || readMeta("slot_time");

    const parts = [label, date, time].filter(Boolean);
    if (parts.length) return parts.join(" • ");
  }

  return null;
}

function sumItemsCount(items: CartItem[]): number {
  return items.reduce((sum, it) => sum + toInt(it?.quantity, 0), 0);
}

function cloneItem(it: CartItem): CartItem {
  return {
    ...it,
    quantity_limits: it.quantity_limits ? { ...it.quantity_limits } : undefined,
    prices: it.prices ? { ...it.prices } : undefined,
    totals: it.totals ? { ...it.totals } : undefined,
    item_data: Array.isArray(it.item_data) ? [...it.item_data] : it.item_data,
    meta_data: Array.isArray(it.meta_data) ? [...it.meta_data] : it.meta_data,
    extensions:
      it.extensions && typeof it.extensions === "object"
        ? { ...(it.extensions as Record<string, unknown>) }
        : it.extensions,
  };
}

function cloneCart(c: Cart | null): Cart | null {
  if (!c) return c;
  return {
    ...c,
    items: Array.isArray(c.items) ? c.items.map(cloneItem) : [],
    totals: c.totals ? { ...c.totals } : c.totals,
  };
}

function getUnitPriceMinor(it: CartItem): number | null {
  return toMinorNumber(it?.prices?.price);
}

function setItemQuantityAndTotals(it: CartItem, qty: number): CartItem {
  const next = cloneItem(it);
  next.quantity = qty;

  const unitMinor = getUnitPriceMinor(next);
  const currencyCode =
    next.totals?.currency_code ?? next.prices?.currency_code ?? undefined;
  const minorUnit =
    next.totals?.currency_minor_unit ?? next.prices?.currency_minor_unit ?? undefined;

  if (unitMinor !== null) {
    const lineMinor = unitMinor * qty;
    next.totals = {
      ...(next.totals ?? {}),
      line_total: String(lineMinor),
      total: String(lineMinor),
      currency_code: currencyCode,
      currency_minor_unit: minorUnit,
    };
  }

  return next;
}

function recalcCartSummary(cart: Cart): Cart {
  const next = cloneCart(cart) as Cart;
  const items = Array.isArray(next.items) ? next.items : [];

  const itemsCount = sumItemsCount(items);
  let subtotalMinor = 0;

  const currencyCode =
    next.totals?.currency_code ??
    items.find((it) => it?.totals?.currency_code)?.totals?.currency_code ??
    items.find((it) => it?.prices?.currency_code)?.prices?.currency_code;

  const minorUnit =
    next.totals?.currency_minor_unit ??
    items.find((it) => typeof it?.totals?.currency_minor_unit === "number")?.totals
      ?.currency_minor_unit ??
    items.find((it) => typeof it?.prices?.currency_minor_unit === "number")?.prices
      ?.currency_minor_unit;

  for (const it of items) {
    const qty = toInt(it?.quantity, 0);
    const unitMinor = getUnitPriceMinor(it);
    const lineMinor =
      unitMinor !== null
        ? unitMinor * qty
        : toMinorNumber(it?.totals?.line_total ?? it?.totals?.total);

    if (lineMinor !== null) {
      subtotalMinor += lineMinor;
    }
  }

  next.items_count = itemsCount;
  next.totals = {
    ...(next.totals ?? {}),
    total_items: String(subtotalMinor),
    total_price: String(subtotalMinor),
    total: String(subtotalMinor),
    currency_code: currencyCode,
    currency_minor_unit: minorUnit,
  };

  return next;
}

function getItemMinQty(it: CartItem): number {
  const min = Number(it?.quantity_limits?.minimum);
  return Number.isFinite(min) && min > 0 ? Math.floor(min) : 1;
}

function getItemMaxQty(it: CartItem): number | null {
  const max = Number(it?.quantity_limits?.maximum);
  return Number.isFinite(max) && max >= 0 ? Math.floor(max) : null;
}

export default function CartPageClient({ lang, initialCart = null }: Props) {
  const { setItemsCount } = useCart();
  const { templateRoutes, global_text } = useLayoutData();
  const checkoutHref = templateRoutes?.["page-checkout.php"];
  const shopHref = templateRoutes?.["page-shop.php"];

  const CART: Record<string, string> =
    (global_text as { cart?: { cart?: Record<string, string> } } | undefined)
      ?.cart?.cart ?? {};
  const t = (k: string, fallback = "") => CART[k] || fallback;

  const initialCartTyped = (initialCart as Cart | null) ?? null;

  const [cart, setCart] = useState<Cart | null>(initialCartTyped);
  const [isLoading, setIsLoading] = useState<boolean>(!initialCartTyped);
  const [isMutating, setIsMutating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const didBootRef = useRef(false);
  const confirmedCartRef = useRef<Cart | null>(initialCartTyped);

  const mutationInFlightRef = useRef(false);
  const queuedQtyRef = useRef<Record<string, number>>({});
  const mutationOrderRef = useRef<string[]>([]);
  const [activeMutationKey, setActiveMutationKey] = useState<string | null>(null);

  const items: CartItem[] = useMemo(() => {
    const arr = cart?.items;
    return Array.isArray(arr) ? arr : [];
  }, [cart]);

  const locale = useMemo(() => resolveLocaleFromLang(lang), [lang]);

  const subtotalFormatted = useMemo(() => {
    return formatMoneyFromValue(
      cart?.totals?.total_items ?? null,
      cart?.totals?.currency_code ?? null,
      cart?.totals?.currency_minor_unit ?? null,
      locale
    );
  }, [cart, locale]);

  const totalFormatted = useMemo(() => {
    return formatMoneyFromValue(
      cart?.totals?.total_price ?? cart?.totals?.total ?? null,
      cart?.totals?.currency_code ?? null,
      cart?.totals?.currency_minor_unit ?? null,
      locale
    );
  }, [cart, locale]);

  const derivedItemsCount = useMemo(() => {
    return toInt(cart?.items_count ?? sumItemsCount(items), 0);
  }, [cart, items]);

  useEffect(() => {
    setItemsCount(derivedItemsCount);
  }, [derivedItemsCount, setItemsCount]);

  function baseHeaders(extra?: Record<string, string>) {
    return {
      Accept: "application/json",
      ...(lang ? { "X-Lang": lang } : {}),
      ...(extra ?? {}),
    };
  }

  function getFriendlyMaxMessage(it: CartItem, maxQty: number) {
    const fallbackItemName = t("fallback_item_name", "This item");
    const name = decodeHtmlEntities(it?.name || fallbackItemName);

    if (maxQty <= 0) {
      return t("error_max_out_of_stock", "{name} is out of stock.").replace("{name}", name);
    }

    return t("error_max_reached", "You've reached the maximum available quantity for {name}.").replace("{name}", name);
  }

  function getItemAvailabilityMessage(it: CartItem): string | null {
    const max = getItemMaxQty(it);
    const qty = toInt(it.quantity, 0);

    if (max === null) return null;
    if (max === 0) return t("availability_out_of_stock", "Out of stock");

    if (qty >= max) {
      // Sold-individually items cap at 1 by design, not because stock ran
      // low. Show a per-customer limit note instead of the generic max-reached
      // message so it doesn't read as "sold out" when there's plenty of stock.
      if (it.sold_individually === true) {
        return t("sold_individually_note", "Limit of 1 ticket per person.");
      }
      return t("availability_max_reached", "Maximum available quantity for this time slot reached.");
    }

    return null;
  }

  function applyQueuedQuantitiesToBase(baseCart: Cart): Cart {
    const next = cloneCart(baseCart) as Cart;
    const nextItems = Array.isArray(next.items) ? [...next.items] : [];

    for (const key of mutationOrderRef.current) {
      const queuedQty = queuedQtyRef.current[key];
      if (queuedQty === undefined) continue;

      const idx = nextItems.findIndex((x) => String(x?.key || "") === key);
      if (idx === -1) continue;

      if (queuedQty <= 0) {
        nextItems.splice(idx, 1);
      } else {
        nextItems[idx] = setItemQuantityAndTotals(nextItems[idx], queuedQty);
      }
    }

    next.items = nextItems;
    return recalcCartSummary(next);
  }

  function syncCart(nextCart: Cart) {
    confirmedCartRef.current = cloneCart(nextCart);
    const merged = applyQueuedQuantitiesToBase(nextCart);
    setCart(merged);
  }

  function rollbackToConfirmed() {
    const confirmed = confirmedCartRef.current;

    if (!confirmed) {
      setCart(null);
      return;
    }

    const restored = applyQueuedQuantitiesToBase(confirmed);
    setCart(restored);
  }

  async function loadCart() {
    setError(null);
    setIsLoading(true);

    try {
      const res = await fetch(apiPath("/api/wc/cart/get"), {
        method: "GET",
        headers: baseHeaders(),
        cache: "no-store",
        credentials: "include",
      });

      if (!res.ok) {
        const requestFailedTpl = t("error_request_failed", "Request failed ({status})");
        const msg = await getErrorMessageFromResponse(res, requestFailedTpl);
        const prefix = t("error_load_prefix", "Failed to load cart ({status})").replace("{status}", String(res.status));
        throw new Error(`${prefix} ${msg}`);
      }

      const json = (await res.json()) as Cart;
      syncCart(json);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : t("error_load_failed", "Could not load cart.");
      setError(msg);
    } finally {
      setIsLoading(false);
    }
  }

  function optimisticApplyQtyChange(itemKey: string, nextQty: number) {
    setCart((prev) => {
      if (!prev || !Array.isArray(prev.items)) return prev;

      const idx = prev.items.findIndex((x) => String(x?.key || "") === itemKey);
      if (idx === -1) return prev;

      const next = cloneCart(prev) as Cart;
      const nextItems = Array.isArray(next.items) ? [...next.items] : [];

      if (nextQty <= 0) {
        nextItems.splice(idx, 1);
      } else {
        nextItems[idx] = setItemQuantityAndTotals(nextItems[idx], nextQty);
      }

      next.items = nextItems;
      return recalcCartSummary(next);
    });
  }

  function enqueueMutation(key: string, quantity: number) {
    if (!(key in queuedQtyRef.current)) {
      mutationOrderRef.current.push(key);
    }

    queuedQtyRef.current[key] = quantity;
  }

  function shiftNextMutation(): PendingMutation | null {
    while (mutationOrderRef.current.length > 0) {
      const key = mutationOrderRef.current[0];
      const quantity = queuedQtyRef.current[key];

      mutationOrderRef.current.shift();

      if (quantity === undefined) continue;

      delete queuedQtyRef.current[key];
      return { key, quantity };
    }

    return null;
  }

  async function flushCartMutations() {
    if (mutationInFlightRef.current) return;

    const nextMutation = shiftNextMutation();
    if (!nextMutation) {
      setIsMutating(false);
      setActiveMutationKey(null);
      return;
    }

    mutationInFlightRef.current = true;
    setIsMutating(true);
    setActiveMutationKey(nextMutation.key);

    const { key, quantity } = nextMutation;

    try {
      let res: Response;

      if (quantity <= 0) {
        res = await fetch(apiPath("/api/wc/cart/remove"), {
          method: "POST",
          headers: baseHeaders({ "Content-Type": "application/json" }),
          body: JSON.stringify({ key }),
          cache: "no-store",
          credentials: "include",
        });
      } else {
        res = await fetch(apiPath("/api/wc/cart/update"), {
          method: "POST",
          headers: baseHeaders({ "Content-Type": "application/json" }),
          body: JSON.stringify({ key, quantity }),
          cache: "no-store",
          credentials: "include",
        });
      }

      const text = await res.text().catch(() => "");
      let json: Cart | MutationErrorPayload | null = null;

      try {
        json = text ? JSON.parse(text) : null;
      } catch {
        json = null;
      }

      if (!res.ok) {
        const payload = json as MutationErrorPayload | null;

        if (payload?.cart) {
          confirmedCartRef.current = cloneCart(payload.cart);
          setCart(cloneCart(payload.cart));
        } else {
          rollbackToConfirmed();
        }

        const message =
          typeof payload?.error === "string" && payload.error.trim()
            ? payload.error
            : typeof payload?.message === "string" && payload.message.trim()
              ? payload.message
              : text || t("error_update_failed", "Could not update cart.");

        throw new Error(message);
      }

      syncCart(json as Cart);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : t("error_update_failed", "Could not update cart.");
      setError(msg);
    } finally {
      mutationInFlightRef.current = false;
      setActiveMutationKey(null);
    }

    if (mutationOrderRef.current.length > 0) {
      void flushCartMutations();
      return;
    }

    setIsMutating(false);
  }

  function queueQtyChange(key: string, nextQty: number) {
    if (!key) return;

    const item = items.find((x) => String(x?.key || "") === key);
    if (!item) return;

    const maxQty = getItemMaxQty(item);
    const safeQty = Math.max(0, Math.floor(nextQty));

    if (maxQty !== null && safeQty > maxQty) {
      setError(getFriendlyMaxMessage(item, maxQty));
      return;
    }

    setError(null);
    enqueueMutation(key, safeQty);
    optimisticApplyQtyChange(key, safeQty);
    void flushCartMutations();
  }

  function changeQtyByDelta(key: string, delta: number) {
    const item = items.find((x) => String(x?.key || "") === key);
    if (!item) return;

    const currentQty = toInt(item.quantity, 0);
    const minQty = getItemMinQty(item);
    const maxQty = getItemMaxQty(item);

    let nextQty: number;

    if (delta < 0) {
      nextQty = currentQty - 1;
      if (currentQty <= minQty) {
        nextQty = 0;
      }
    } else {
      nextQty = currentQty + 1;
    }

    nextQty = Math.max(0, Math.floor(nextQty));

    if (maxQty !== null && nextQty > maxQty) {
      setError(getFriendlyMaxMessage(item, maxQty));
      return;
    }

    if (nextQty === currentQty) {
      if (delta > 0 && maxQty !== null && currentQty >= maxQty) {
        setError(getFriendlyMaxMessage(item, maxQty));
      }
      return;
    }

    queueQtyChange(key, nextQty);
  }

  useEffect(() => {
    if (didBootRef.current) return;
    didBootRef.current = true;

    if (initialCartTyped) {
      confirmedCartRef.current = cloneCart(initialCartTyped);
      setCart(cloneCart(initialCartTyped));
      setIsLoading(false);
      return;
    }

    void loadCart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Section>
      <Wrapper>
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
          <div className="lg:col-span-8">
            {error ? (
              <div className="mb-4 overflow-hidden">
                <div className="border-t-4 border-fuji-red bg-fuji-grey px-5 py-4">
                  <div className="font-bold">{t("error_generic_title", "Something went wrong")}</div>
                  <div className="mt-1 text-sm opacity-80">
                    <SafeHtml content={error} />
                  </div>
                </div>
              </div>
            ) : null}

            {isLoading && items.length === 0 ? (
              <div className="rounded-2xl border p-6 text-sm opacity-80">
                {t("loading_message", "Loading cart…")}
              </div>
            ) : null}

            {items.length > 0 && (
              <div className="overflow-hidden border-b">
                <div className="grid grid-cols-12 border-t-4 border-fuji-green bg-fuji-grey px-6 py-4 font-bold">
                  <div className="col-span-6">{t("table_product", "Product")}</div>
                  <div className="col-span-2 text-center">{t("table_price", "Price")}</div>
                  <div className="col-span-2 text-center">{t("table_quantity", "Quantity")}</div>
                  <div className="col-span-2 text-right">{t("table_subtotal", "Subtotal")}</div>
                </div>

                {items.map((it, idx) => {
                  const key = String(it?.key || `${idx}`);
                  const qty = toInt(it?.quantity, 1);
                  const maxQty = getItemMaxQty(it);
                  const canIncrease = maxQty === null ? true : qty < maxQty;
                  const canDecrease = qty > 0;
                  const availabilityMessage = getItemAvailabilityMessage(it);
                  const isRowBusy =
                    activeMutationKey === key || Object.prototype.hasOwnProperty.call(queuedQtyRef.current, key);

                  const slot = parseSlotFromItemData(it);
                  const slotDate = slot?.dateISO ? formatISODate(slot.dateISO, locale) : "";
                  const slotTime = slot?.time || "";
                  const slotLine = [slotDate, slotTime].filter(Boolean).join(" • ");

                  const slotSummaryFallback = !slot ? getSlotSummaryFallback(it) : null;

                  const price = formatMoneyFromValue(
                    it?.prices?.price ?? null,
                    it?.prices?.currency_code ?? null,
                    it?.prices?.currency_minor_unit ?? null,
                    locale
                  );

                  const lineTotal = formatMoneyFromValue(
                    it?.totals?.line_total ?? it?.totals?.total ?? null,
                    it?.totals?.currency_code ?? null,
                    it?.totals?.currency_minor_unit ?? null,
                    locale
                  );

                  return (
                    <div
                      key={key}
                      className={`grid grid-cols-12 items-center border-b px-6 py-6 last:border-none ${isRowBusy ? "opacity-80" : ""
                        }`}
                    >
                      <div className="col-span-6">
                        <div className="font-bold">
                          {it?.permalink ? (
                            <Link
                              href={it.permalink}
                              className="hover:opacity-70 transition-opacity"
                            >
                              <SafeHtml content={it?.name} />
                            </Link>
                          ) : (
                            <SafeHtml content={it?.name} />
                          )}
                        </div>

                        {slot ? (
                          <div className="mt-1 text-sm opacity-70">
                            {slotLine ? <div>{slotLine}</div> : null}
                          </div>
                        ) : slotSummaryFallback ? (
                          <div className="mt-1 text-sm opacity-70">{slotSummaryFallback}</div>
                        ) : null}

                        {availabilityMessage ? (
                          <div className={`mt-2 text-sm ${it.sold_individually === true ? "opacity-70" : "text-fuji-red"
                            }`}>
                            {availabilityMessage}
                          </div>
                        ) : null}
                      </div>

                      <div className="col-span-2 text-center">{price}</div>

                      <div className="col-span-2 flex justify-center">
                        <div className="flex items-center overflow-hidden rounded-lg border">
                          <button
                            type="button"
                            className="border-r px-3 py-1 cursor-pointer transition-opacity hover:opacity-80 disabled:cursor-not-allowed disabled:opacity-50"
                            onClick={() => changeQtyByDelta(key, -1)}
                            aria-label={t("qty_decrease_aria", "Decrease quantity")}
                            disabled={!canDecrease}
                          >
                            -
                          </button>

                          <span className="px-4 tabular-nums">{qty}</span>

                          <button
                            type="button"
                            className="border-l px-3 py-2 cursor-pointer transition-opacity hover:opacity-80 disabled:cursor-not-allowed disabled:opacity-50"
                            onClick={() => changeQtyByDelta(key, 1)}
                            aria-label={t("qty_increase_aria", "Increase quantity")}
                            disabled={!canIncrease}
                            title={!canIncrease ? t("qty_max_reached_title", "Maximum available quantity reached") : undefined}
                          >
                            +
                          </button>
                        </div>
                      </div>

                      <div className="col-span-2 text-right font-medium">{lineTotal}</div>
                    </div>
                  );
                })}
              </div>
            )}

            {items.length === 0 && !isLoading && (
              <div className="rounded-2xl border p-8 text-center">
                <p className="text-lg font-medium">{t("empty_message", "Your cart is empty")}</p>
                <Link href={shopHref || ""} className="mt-4 inline-block rounded-xl border px-6 py-3">
                  {t("view_events_button", "View events")}
                </Link>
              </div>
            )}
          </div>

          <div className="lg:col-span-4">
            <div>
              <div className="overflow-hidden border-b">
                <div className="border-t-4 border-fuji-green bg-fuji-grey px-6 py-4 font-bold">
                  {t("summary_heading", "Cart total")}
                </div>

                <div className="border-b px-6 py-5 last:border-none">
                  <div className="flex justify-between">
                    <span className="font-bold opacity-80">{t("summary_subtotal", "Subtotal")}</span>
                    <span className="font-medium">{subtotalFormatted}</span>
                  </div>
                </div>

                <div className="border-b px-6 py-5 last:border-none">
                  <div className="flex justify-between text-base font-bold">
                    <span>{t("summary_total", "Total")}</span>
                    <span>{totalFormatted}</span>
                  </div>
                </div>
              </div>

              <Button
                as="link"
                href={items.length > 0 ? checkoutHref || "" : "#"}
                type="button"
                className="mt-4 w-full text-body"
                bgStyle="bg-fuji-green hover:bg-fuji-green/80 border-fuji-green hover:border-fuji-green/80"
                center
                disabled={items.length === 0 || isLoading || isMutating}
                aria-disabled={items.length === 0 || isLoading || isMutating}
              >
                {t("checkout_button", "Continue to checkout")}
              </Button>
            </div>
          </div>
        </div>
      </Wrapper>
    </Section>
  );
}